import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Switching a tab is not "going somewhere" — it swaps a panel in place, and the
// tab strip the user just clicked is usually the thing they want to keep looking
// at. Pages that carry their active tab in the query string (tournament detail,
// academy, marketplace) call setSearchParams({ tab }), which react-router treats
// as a navigation with a fresh `key`, so an unfiltered scroll-to-top yanked the
// page back up to the hero image on every single tab click.
const isTabOnlyChange = (prev, next) => {
  if (!prev || prev.pathname !== next.pathname) return false;

  const a = new URLSearchParams(prev.search);
  const b = new URLSearchParams(next.search);
  a.delete('tab');
  b.delete('tab');

  // Same path, and everything except `tab` is identical -> a tab switch.
  // Sorted so parameter order can't produce a false negative.
  const norm = (p) => [...p.entries()].sort().map(([k, v]) => `${k}=${v}`).join('&');
  return norm(a) === norm(b);
};

const ScrollToTop = () => {
  // `key` changes on every navigation entry, including query-string-only changes
  // (e.g. opening a live game via `?eventId=...&gameId=...&openGame=true` on the
  // same pathname) — watching only `pathname` misses those.
  const location = useLocation();
  const prevLocation = useRef(null);

  useEffect(() => {
    const prev = prevLocation.current;
    prevLocation.current = location;

    // Everything else — real navigations, and same-path query changes like
    // opening a live game — still scrolls to top as before.
    if (isTabOnlyChange(prev, location)) return;

    window.scrollTo(0, 0);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default ScrollToTop;
